import { Workout } from '@strength/domain-workout';
import { DateTime } from 'luxon';
import { DateRange } from '../../../../components/dates/RangeCalendar';
import {
  BOWLING_METRICS,
  getRepsValueForMetricKeyword,
} from '../slots/workout-metric-utils';
import {
  ACUTE_WINDOW_DAYS,
  CHRONIC_WINDOW_DAYS,
  TEN_WEEK_WINDOW_DAYS,
} from './chart-utils';

export type LoadDay = {
  day: string;
  load: number;
  sevenDayAvg: number;
  twentyEightDayAvg: number;
  tenWeekAvg: number;
  /** % difference between the 7-day and 28-day averages: (7d − 28d) / 28d × 100. Null when the 28-day average is 0. */
  percentDifference: number | null;
  bandMin: number;
  bandMax: number;
  /** [min, max] band range where 7-day is higher (purple); zero height where 28-day is higher */
  bandPurpleRange: [number, number];
  /** [min, max] band range where 28-day is higher (pink); zero height where 7-day is higher */
  bandPinkRange: [number, number];
};

function getLoadForWorkout(workout: Workout): number {
  return BOWLING_METRICS.reduce((sum, keyword) => {
    const { value } = getRepsValueForMetricKeyword(keyword, workout);
    const n = value !== undefined ? Number(value) : 0;
    return sum + (Number.isNaN(n) ? 0 : n);
  }, 0);
}

function sumLoadsInWindow(
  loadMap: Map<string, number>,
  endDay: string,
  windowSize: number,
): number {
  const end = DateTime.fromISO(endDay);
  let sum = 0;
  for (let i = 0; i < windowSize; i++) {
    const key = end.minus({ days: windowSize - 1 - i }).toISODate() ?? '';
    sum += loadMap.get(key) ?? 0;
  }
  return sum;
}

export function aggregateDailyLoads(workouts: Workout[]): Map<string, number> {
  const dailyTotals = new Map<string, number>();
  for (const workout of workouts) {
    if (!workout || workout.assignedDate == null) continue;
    const day = workout.assignedDate;
    const load = getLoadForWorkout(workout);
    dailyTotals.set(day, (dailyTotals.get(day) ?? 0) + load);
  }
  return dailyTotals;
}

/**
 * Emits one LoadDay per day in `dateRange`. Rolling 7d/28d/70d sums use
 * `dailyTotals` directly, so loads from days BEFORE `dateRange.start` still
 * contribute to the rolling averages at the start of the visible range.
 * Loads on days outside the range never appear as their own entry.
 */
export function expandToLoadDays(
  dailyTotals: Map<string, number>,
  dateRange: DateRange,
): LoadDay[] {
  const start = DateTime.fromISO(dateRange.start);
  const end = DateTime.fromISO(dateRange.end);
  if (!start.isValid || !end.isValid || start > end) return [];

  const result: LoadDay[] = [];
  for (let d = start; d <= end; d = d.plus({ days: 1 })) {
    const day = d.toISODate();
    if (!day) continue;
    const load = dailyTotals.get(day) ?? 0;
    const sevenDaySum = sumLoadsInWindow(dailyTotals, day, ACUTE_WINDOW_DAYS);
    const twentyEightDaySum = sumLoadsInWindow(
      dailyTotals,
      day,
      CHRONIC_WINDOW_DAYS,
    );
    const tenWeekSum = sumLoadsInWindow(dailyTotals, day, TEN_WEEK_WINDOW_DAYS);

    // Get the averages for each line
    // (the sum / number of weeks)
    const sevenDayAvg = sevenDaySum;
    const twentyEightDayAvg = twentyEightDaySum / 4;
    const tenWeekAvg = tenWeekSum / 10;

    // (7d − 28d) / 28d × 100 — per Cricket Aus: how much the acute load
    // deviates from the chronic average. Undefined when the 28-day average is 0.
    const percentDifference =
      twentyEightDayAvg === 0
        ? null
        : ((sevenDayAvg - twentyEightDayAvg) / twentyEightDayAvg) * 100;

    // Load-spike banding compares only the 7-day/28-day averages; the
    // 70-day line is informational and doesn't affect which band renders.
    const bandMin = Math.min(sevenDayAvg, twentyEightDayAvg);
    const bandMax = Math.max(sevenDayAvg, twentyEightDayAvg);
    const sevenDayOnTop = sevenDayAvg >= twentyEightDayAvg;
    result.push({
      day,
      load,
      sevenDayAvg,
      twentyEightDayAvg,
      tenWeekAvg,
      percentDifference,
      bandMin,
      bandMax,
      bandPurpleRange: sevenDayOnTop ? [bandMin, bandMax] : [bandMin, bandMin],
      bandPinkRange: !sevenDayOnTop ? [bandMin, bandMax] : [bandMin, bandMin],
    });
  }

  return result;
}

/** Days are flagged when the 7-day average exceeds the 28-day average by more than this percent. */
export const LOAD_SPIKE_THRESHOLD_PERCENT = 50;

/**
 * Returns the ISO days that start a load-spike run: days where the % difference
 * (7-day vs 28-day average) exceeds LOAD_SPIKE_THRESHOLD_PERCENT, keeping only
 * the FIRST day of each consecutive run of qualifying days — a 3-day spike
 * yields one flag, not three.
 */
export function computeLoadSpikeDays(
  data: LoadDay[],
  thresholdPercent: number = LOAD_SPIKE_THRESHOLD_PERCENT,
): string[] {
  const spikeDays: string[] = [];
  let inRun = false;
  for (const row of data) {
    const isSpike =
      row.percentDifference != null && row.percentDifference > thresholdPercent;
    if (isSpike && !inRun) spikeDays.push(row.day);
    inRun = isSpike;
  }
  return spikeDays;
}

/**
 * Drops workouts whose day is strictly before `todayIso` and whose
 * `workoutId` has no recorded actual. A past day with only a
 * planned/prescribed value must not bleed into the 7/28/70-day rolling
 * averages; today and future days are unaffected — they keep contributing
 * their planned value until an actual is logged.
 */
export function excludePastPlannedOnlyWorkouts(
  workouts: Workout[],
  hasActual: (workoutId: string) => boolean,
  todayIso: string,
): Workout[] {
  return workouts.filter((workout) => {
    if (!workout || workout.assignedDate == null) return true;
    if (workout.assignedDate >= todayIso) return true;
    return hasActual(workout.workoutId);
  });
}

export function computeLoadDays(
  workouts: Workout[],
  dateRange: DateRange,
  hasActual: (workoutId: string) => boolean,
): LoadDay[] {
  const todayIso = DateTime.now().toISODate() ?? '';
  const eligibleWorkouts = excludePastPlannedOnlyWorkouts(
    workouts,
    hasActual,
    todayIso,
  );
  return expandToLoadDays(aggregateDailyLoads(eligibleWorkouts), dateRange);
}
